import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, fileUrl } from "@/lib/api";
import { formatPrice } from "@/lib/currency";
import Footer from "@/components/Footer";
import {
  CheckCircle2, Loader2, Car, CalendarDays, MapPin, Truck, Fuel, ShieldCheck, Building2,
} from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?crop=entropy&cs=srgb&fm=jpg&q=85";

const DEPOSIT_LABEL = {
  authorized: { text: "Authorized (on hold)", cls: "text-amber-400 border-amber-400/30" },
  released: { text: "Released", cls: "text-emerald-400 border-emerald-400/30" },
  captured: { text: "Captured (damage)", cls: "text-[#FF5E7E] border-[#FF5E7E]/30" },
};

export default function CarBookingConfirmation() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/car-bookings/${id}`).then((r) => setB(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return (
    <div className="min-h-screen bg-[#060810] flex flex-col items-center justify-center px-4 text-center">
      <p className="text-white font-heading text-xl">Booking not found</p>
      <Link to="/dashboard" className="btn-neon rounded-full px-6 py-2.5 font-semibold text-sm mt-4">Go to dashboard</Link>
    </div>
  );
  if (!b) return <div className="min-h-screen bg-[#060810] flex justify-center pt-40"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;

  const dep = DEPOSIT_LABEL[b.deposit?.status] || DEPOSIT_LABEL.authorized;
  const qrPayload = JSON.stringify({ t: "carivisor-car", b: b.id, code: b.checkin_code });

  return (
    <div className="min-h-screen bg-[#060810]" data-testid="car-confirmation-page">
      <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto pt-12 pb-16">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-white">You're all set!</h1>
          <p className="text-slate-300 mt-2">Your car rental is confirmed. <span className="text-gradient-cool font-semibold">See it. Book it. Go.</span></p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 glass rounded-2xl overflow-hidden">
            <div className="aspect-[16/7] bg-[#0B0E1B]">
              <img src={b.car_image ? fileUrl(b.car_image) : FALLBACK} alt={b.car_name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = FALLBACK; }} />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full glass text-cyan-300">{b.car_type}</span>
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full glass text-slate-300">{b.status}</span>
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mt-3">{b.car_name}</h2>
              <p className="flex items-center gap-1.5 text-slate-400 text-sm mt-1"><Building2 className="w-4 h-4 text-violet-400" /> {b.provider_name}</p>

              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <Info icon={CalendarDays} label="Pickup" value={b.pickup_date} />
                <Info icon={CalendarDays} label="Drop-off" value={b.dropoff_date} />
                <Info icon={b.delivery_option === "delivery" ? Truck : Building2} label="Pickup method" value={b.delivery_option === "delivery" ? "Delivered to you" : "Branch pickup"} />
                <Info icon={Fuel} label="Fuel policy" value={b.fuel_policy} />
                {b.delivery_option === "delivery" && (b.delivery_address || b.flight_number) && (
                  <Info icon={MapPin} label="Delivery" value={b.delivery_address || `Flight ${b.flight_number}`} />
                )}
                <Info icon={MapPin} label="Island" value={b.island} />
              </div>

              <div className="mt-6 border-t border-[#1D2640] pt-4 space-y-1.5 text-sm">
                <Row label={`Rental (${b.days} day(s))`} value={formatPrice(b.rental, b.currency)} />
                {b.delivery_fee > 0 && <Row label="Delivery fee" value={formatPrice(b.delivery_fee, b.currency)} />}
                <Row label="Platform fee (20%)" value={formatPrice(b.platform_fee, b.currency)} muted />
                {b.discount > 0 && <Row label="Promo discount" value={`- ${formatPrice(b.discount, b.currency)}`} green />}
                <div className="flex justify-between pt-2 border-t border-[#1D2640] mt-2">
                  <span className="text-white font-semibold">Total paid</span>
                  <span className="font-heading font-extrabold text-white text-xl">{formatPrice(b.total, b.currency)}</span>
                </div>
              </div>

              <div className="mt-5 flex items-start gap-2 rounded-xl bg-cyan-400/5 border border-cyan-400/20 px-3 py-3">
                <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-white font-semibold">Security deposit hold</p>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${dep.cls}`} data-testid="deposit-status">{dep.text}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {formatPrice(b.deposit?.amount, b.currency)} authorized on your card. Released automatically once the provider confirms the vehicle was returned undamaged.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="glass neon-border-cool rounded-2xl p-6 text-center h-fit sticky top-24" data-testid="car-qr-card">
            <p className="text-xs font-mono-caption uppercase tracking-[0.25em] text-cyan-300 mb-4">Pickup code</p>
            <div className="bg-white rounded-2xl p-4 inline-block">
              <QRCodeSVG value={qrPayload} size={168} />
            </div>
            <p className="font-heading font-bold text-white text-lg tracking-[0.2em] mt-4">{b.checkin_code}</p>
            <p className="text-xs text-slate-400 mt-1">Show this at vehicle pickup</p>
            <Link to="/dashboard" className="block w-full btn-cool rounded-xl py-2.5 font-semibold text-sm mt-5">View my bookings</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 text-cyan-300 mt-0.5 shrink-0" />
      <div>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-sm text-white">{value}</p>
      </div>
    </div>
  );
}
function Row({ label, value, muted, green }) {
  return (
    <div className="flex justify-between">
      <span className={muted ? "text-slate-500" : "text-slate-400"}>{label}</span>
      <span className={green ? "text-emerald-400" : "text-slate-200"}>{value}</span>
    </div>
  );
}
